import { useState, useEffect } from 'react';
import { Button, QrCard } from '@indipia/ui-core';
import { photoApi } from '../api/photoApi';

// QR 완전 핸드오프 — QR을 찍는 순간 PC는 손을 뗀다(전환 설계 v1.0 §7-2).
// 폰은 자기 계정으로 로그인한 인디포토 사용자가 되어 업로드·분류·태그를 폰에서 끝낸다.
// 따라서 이 패널은 사진을 받지 않는다 — 받는 것은 "폰이 이어받았다"는 시그널 하나뿐이다.
//
// 껍데기·QR·텍스트 슬롯은 공용 QrCard(D-09 §15.45)가 갖는다.
// 이 파일에 남는 것은 세션 상태(타이머·SSE·3-phase)와 문구뿐이다.

// 고정 딥링크 — 토큰도 세션도 싣지 않는다. 호스트는 앱 flavor의 appLinkHost와 일치해야
// App Links가 앱을 연다(dev.indipia.ai / indipia.ai). 앱이 없으면 웹의 스토어 안내 페이지로 착지한다.
const HANDOFF_DEEP_LINK = `${import.meta.env.VITE_DEEP_LINK_BASE ?? ''}/u/photo-upload`;

// QR 표시 수명 — 서버 만료가 아니라 화면 표시용 타이머다(고정 딥링크라 만료 개념 자체가 없다).
// 시그널이 닿지 않는 경우(폰이 다른 계정으로 로그인 등)의 폴백 — 이 시간이 지나면 QR을 접는다.
const QR_DISPLAY_SECONDS = 180;

// SSE 재연결 — 지수 백오프. 폴링 폴백은 두지 않는다(조회할 누적 상태가 없다 — C-04 §9.3의 대상이 아님).
const SSE_RECONNECT_BASE_MS = 1000;
const SSE_RECONNECT_MAX_MS = 16000;

type Phase = 'active' | 'expired' | 'handed-off';

// 진입 동선(HomePage "오늘은 어떤 일부터 시작해볼까요?" 항목 → ?entry=)에 따라 문구·구성이 달라지므로
// 표시 문구는 전부 props로 받는다. 기본값은 entry 없이 들어온 현행 구성의 문구를 그대로 유지한다.
interface QrHandoffPanelProps {
  style?: React.CSSProperties;
  /** 패널 제목 */
  title?: string;
  /** QR **위** 보조 안내 (기본 없음) */
  subtitle?: string;
  /** QR **아래** 안내 */
  description?: React.ReactNode;
  /** description 을 안내문(보조색)이 아니라 행동 지시문(강조)으로 표시한다 */
  descriptionStrong?: boolean;
  /** 만료 시 타이머 자리에 표시할 문구. 주면 QR 아래 별도 만료 안내는 생략한다. */
  expiredLabel?: string;
}

export function QrHandoffPanel({
  style,
  title = '휴대폰으로 이어서 하기',
  subtitle,
  description = <>휴대폰 카메라로 QR을 스캔하면<br />앱에서 사진 올리기가 바로 열립니다</>,
  descriptionStrong = false,
  expiredLabel,
}: QrHandoffPanelProps) {
  const [phase, setPhase] = useState<Phase>('active');
  const [remainingSec, setRemainingSec] = useState(QR_DISPLAY_SECONDS);

  // 표시 카운트다운 — active 에서만 진행. 감소만 담당한다.
  // setState 업데이터 안에서 다른 setState(setPhase)를 부르면 업데이터가 순수하지 않아
  // StrictMode 이중 호출에서 전환이 유실된다(00:00 인데 QR이 그대로 남는 증상). 전환은 아래 효과가 맡는다.
  useEffect(() => {
    if (phase !== 'active') return;
    const timer = setInterval(() => {
      setRemainingSec(prev => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [phase]);

  // 0초 도달 → 만료 전환. 이미 폰이 이어받았으면(handed-off) 무관하다.
  useEffect(() => {
    if (phase === 'active' && remainingSec === 0) setPhase('expired');
  }, [phase, remainingSec]);

  // 회원 채널 구독 — 폰이 딥링크로 진입하면 HANDOFF_STARTED 가 온다.
  // 마운트당 1회 구독하고, 끊기면 지수 백오프로 재연결한다.
  useEffect(() => {
    let closed = false;
    let es: EventSource | null = null;
    let retryCount = 0;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const connect = () => {
      if (closed) return;
      es = photoApi.subscribeHandoffMemberEvents();

      es.addEventListener('HANDOFF_STARTED', () => {
        setPhase('handed-off');
      });

      es.onopen = () => { retryCount = 0; };

      es.onerror = () => {
        es?.close();
        if (closed) return;
        retryCount += 1;
        const delay = Math.min(SSE_RECONNECT_BASE_MS * 2 ** (retryCount - 1), SSE_RECONNECT_MAX_MS);
        retryTimer = setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      closed = true;
      es?.close();
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, []);

  const mm = String(Math.floor(remainingSec / 60)).padStart(2, '0');
  const ss = String(remainingSec % 60).padStart(2, '0');

  const showAgain = () => {
    setRemainingSec(QR_DISPLAY_SECONDS);
    setPhase('active');
  };

  // 인계 상태에서는 QR 을 그리지 않는다 — 폰이 이미 이어받았다.
  // 만료 문구는 타이머와 같은 자리(caption)에 오고, 재발급 버튼은 QR 위 오버레이로 유지된다.
  const handedOff = phase === 'handed-off';

  return (
    <QrCard
      style={style}
      title={title}
      subtitle={subtitle}
      value={handedOff ? undefined : HANDOFF_DEEP_LINK}
      expired={phase === 'expired'}
      overlay={
        <Button variant="primary" onClick={showAgain}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 12a9 9 0 1 1-6.219-8.56" /><polyline points="21 3 21 9 15 9" />
          </svg>
          QR 다시 보기
        </Button>
      }
      description={
        phase === 'active'
          ? description
          : phase === 'expired' && !expiredLabel
            ? 'QR을 숨겼습니다. 다시 보시려면 눌러 주세요'
            : undefined
      }
      descriptionStrong={phase === 'active' && descriptionStrong}
      caption={phase === 'active' ? `${mm}:${ss} 후 QR 숨김` : phase === 'expired' ? expiredLabel : undefined}
      captionTone={phase === 'active' && remainingSec <= 30 ? 'danger' : 'hint'}
    >
      {handedOff ? (
        <>
          <span className="qr-chip">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="5" y="2" width="14" height="20" rx="2" /><line x1="12" y1="18" x2="12.01" y2="18" />
            </svg>
          </span>
          {/* caption 은 행간 100% 라 <br/> 로 접으면 줄이 붙는다 — 요소를 나눠 .qr-text 갭 8 을 쓴다(D-09 §15.45) */}
          <div className="qr-text">
            <div className="qr-desc is-strong">휴대폰에서 이어서 진행 중입니다</div>
            <div className="qr-desc">업로드·분류·태그는 휴대폰에서 끝냅니다</div>
            <div className="qr-desc">이 화면에서는 더 이상 진행되지 않습니다</div>
          </div>
        </>
      ) : undefined}
    </QrCard>
  );
}
